import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Logo } from "../../components/common/Logo";

interface MaintenanceGatePageProps {
    onAccessGranted: () => void;
    onVerify: (code: string) => Promise<void>;
    error?: string;
}

/**
 * Full-page gate that blocks access to the app when maintenance mode is enabled.
 * Shows a code entry form that grants access when the correct code is entered.
 */
export const MaintenanceGatePage = ({
    onAccessGranted,
    onVerify,
    error: externalError
}: MaintenanceGatePageProps) => {
    const { t } = useTranslation('common');
    const [code, setCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(externalError || null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!code.trim()) {
            setError(t('maintenance.pleaseEnterCode'));
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await onVerify(code);
            onAccessGranted();
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || t('maintenance.invalidCode'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background-page flex flex-col">
            {/* Navigation */}
            <nav className="container-custom py-4 px-4 sm:py-6 sm:px-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <Logo className="w-6 h-6" />
                        <span className="text-lg sm:text-2xl font-medium font-display text-text-primary">
                            Dashbal
                        </span>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
                        {/* Lock Icon */}
                        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="w-10 h-10 text-indigo-600" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_8697cb8f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8697cb8f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-semibold text-text-primary mb-2">
                            {t('maintenance.title')}
                        </h1>
                        <p className="text-neutral-600 mb-8">
                            {t('maintenance.description')}
                        </p>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-4 text-left">
                            <div>
                                <label
                                    htmlFor="access-code"
                                    className="sr-only"
                                >
                                    {t('maintenance.accessCode')}
                                </label>
                                <input
                                    id="access-code"
                                    type="password"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder={t('maintenance.enterCode') as string}
                                    className={`
                                        w-full px-4 py-3 rounded-lg border bg-white 
                                        text-text-primary placeholder:text-neutral-400
                                        focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary
                                        transition-all duration-200
                                        ${error ? 'border-error-300 bg-error-50' : 'border-neutral-200'}
                                    `}
                                    autoComplete="off"
                                    autoFocus
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="flex items-center gap-2 text-error-600 text-sm bg-error-50 rounded-lg px-4 py-3">
                                    <span className="w-5 h-5 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_5279c757.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_5279c757.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn-primary w-full py-3 flex justify-center items-center"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                        {t('maintenance.verifying')}
                                    </>
                                ) : (
                                    t('maintenance.accessApp')
                                )}
                            </button>
                        </form>

                        {/* Info */}
                        <p className="text-xs text-neutral-500 mt-6">
                            {t('maintenance.contactAdmin')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-neutral-200 py-6">
                <div className="container-custom text-center">
                    <p className="text-sm text-neutral-500">
                        {t('maintenance.footer', { year: new Date().getFullYear() })}
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default MaintenanceGatePage;
